import { askDate, type NextServiceAnchor, type NextServiceAskRules } from '../core/ask.js';
import type {
  MobilityNextServicesBlock,
  RentalNextServicesBlock,
} from '@voll-intelligence/genui-contract';

/**
 * O pós-carro e o pós-corrida (#5514) ancoram num LUGAR (a retirada do carro,
 * o destino da corrida), não numa cidade: os dois domínios não carregam a
 * cidade. `flight`, `seat`, `train` e `bus` ficam fora dos catálogos default,
 * mas o agents pode injetar qualquer id do enum e toda linha precisa de pedido.
 */
export const STAY_ASK_RULES: NextServiceAskRules = {
  hotel: {
    window: {
      range: 'nextServices.ask.hotelRange',
      open: 'nextServices.ask.hotelOpen',
      bare: 'nextServices.ask.hotelBare',
      trip: 'nextServices.ask.hotelTrip',
    },
  },
  car: {
    window: {
      range: 'nextServices.ask.carRange',
      open: 'nextServices.ask.carOpen',
      bare: 'nextServices.ask.carBare',
      trip: 'nextServices.ask.carTrip',
    },
  },
  insurance: { fixed: 'nextServices.ask.insurance' },
  flight: { fixed: 'nextServices.ask.flight' },
  seat: { fixed: 'nextServices.ask.seat' },
  train: { fixed: 'nextServices.ask.train' },
  bus: { fixed: 'nextServices.ask.bus' },
  transfer_to_airport: { fixed: 'nextServices.ask.transferToAirport' },
  transfer_airport_hotel: { fixed: 'nextServices.ask.transferAirportHotel' },
  transfer_to_hotel: { fixed: 'nextServices.ask.transferToHotel' },
};

/** Um endereço ou o nome de um lugar, nunca inventado: sem ele o pedido é
 *  «para essa viagem». */
function placeAnchor(
  place: string | undefined,
  from: string | undefined,
  to: string | undefined,
  locale: string,
): NextServiceAnchor {
  return {
    param: 'place',
    value: place ? place : null,
    from: askDate(from, locale),
    to: askDate(to, locale),
  };
}

/** Onde fica o carro: o hotel é pedido perto da retirada, nas datas do contrato. */
export function rentalAnchor(block: RentalNextServicesBlock, locale: string): NextServiceAnchor {
  const rental = block.items.find((item) => item.productType === 'rental')?.detail;
  return placeAnchor(
    rental?.pickUpAddress ?? rental?.pickUpAgency,
    rental?.pickupAt,
    rental?.dropoffAt,
    locale,
  );
}

/** Onde a corrida deixa a pessoa: o destino da ida (o carro com motorista não
 *  tem destino, então fica a partida), do dia da ida ao da volta. */
export function mobilityAnchor(
  block: MobilityNextServicesBlock,
  locale: string,
): NextServiceAnchor {
  const ride = block.items.find((item) => item.productType === 'mobility')?.detail;
  return placeAnchor(
    ride?.outward.to ?? ride?.outward.from,
    ride?.outward.scheduleDate,
    ride?.return?.scheduleDate,
    locale,
  );
}
